/**
 * Authenticated fetch wrapper for backend API calls.
 * Uses local JWT token (from email/password auth) or Azure AD token.
 */
import { BACKEND_URL } from './config.js';
import { acquireBackendToken, isMsalConfigured } from './msalConfig.js';
import { getAuthToken, clearAuth } from './authStorage.js';
import { getClientId } from './clientId.js';
import { reportSaveFailure } from './saveFailureNotice.js';
import { isTaskWrite, notifyTasksChanged } from './tasksChanged.js';
import { quickViewBlocked, quickViewResponse } from './quickViewGuard.js';

// A failed WRITE to the app's data is always said (utils/saveFailureNotice):
// many callers only log it, so the change looked saved and vanished on
// reload. Reads are not reported; neither are 401 (the sign-in flow), 409 /
// 412 (compare-and-swap retries handle them) or `options.quiet` requests.
const WRITE_METHODS = new Set(['PUT', 'PATCH', 'POST', 'DELETE']);
const SAVE_PATH_PREFIXES = [
  '/api/projects', '/api/objects', '/api/regions', '/api/ocr', '/api/twin',
  '/api/orgs', '/api/me/symbols', '/api/me/profile', '/api/pdf/save-markups', '/api/files',
];
function isSaveRequest(url, options) {
  if (options?.quiet) return false;
  const method = String(options?.method || 'GET').toUpperCase();
  if (!WRITE_METHODS.has(method)) return false;
  let path = url;
  try { path = new URL(url, 'http://local').pathname; } catch { /* keep as given */ }
  return SAVE_PATH_PREFIXES.some(prefix => path.startsWith(prefix));
}

let cachedMsalToken = null;
let msalTokenExpiry = 0;

async function getToken() {
  // Priority 1: local JWT token (from email/password sign-in)
  const localToken = getAuthToken();
  if (localToken) return localToken;

  // Priority 2: Azure AD token
  if (!isMsalConfigured) return null;
  if (cachedMsalToken && Date.now() < msalTokenExpiry) return cachedMsalToken;
  cachedMsalToken = await acquireBackendToken();
  msalTokenExpiry = Date.now() + 4 * 60 * 1000;
  return cachedMsalToken;
}

// Same token the HTTP layer sends (local JWT or Azure AD) — exported so
// the WebSocket clients authenticate with the identical credential.
export async function getBackendToken() {
  return getToken();
}

// Public auth pages where a 401 is normal flow (expired link, wrong creds)
// and we must not redirect — that would just bounce the user away from
// the page that's trying to recover their session.
const AUTH_FLOW_PATHS = ['/login', '/verify', '/reset-password', '/join'];
let _redirectingOn401 = false;

function handleUnauthorized() {
  if (_redirectingOn401) return;
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (AUTH_FLOW_PATHS.some(p => path === p || path.startsWith(p + '/'))) return;
  _redirectingOn401 = true;
  clearAuth();
  // Full reload so any in-memory caches (cachedUser, prefs, etc.) are dropped.
  window.location.href = '/login';
}

function onAuthFlowPage() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return AUTH_FLOW_PATHS.some(p => path === p || path.startsWith(p + '/'));
}

// Is the local JWT session ACTUALLY dead, or was that 401 endpoint-specific?
// One cheap check against /api/auth/me with the same token. We only sign the
// user out when this confirms the session is gone — a single odd 401 (a backend
// route wrongly returning 401, a transient blip during a long pipeline) must NOT
// nuke a valid session. Network errors → treat as valid (don't sign out on a blip).
async function localSessionStillValid(localToken) {
  try {
    const r = await fetch(`${BACKEND_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${localToken}` },
    });
    return r.status !== 401;
  } catch { return true; }
}

// Force a fresh MSAL/Azure token (drop our cache + re-acquire). Returns the new
// token, or null if re-acquisition fails.
async function refreshMsalToken() {
  cachedMsalToken = null;
  msalTokenExpiry = 0;
  try {
    if (!isMsalConfigured) return null;
    cachedMsalToken = await acquireBackendToken();
    if (cachedMsalToken) msalTokenExpiry = Date.now() + 4 * 60 * 1000;
    return cachedMsalToken || null;
  } catch { return null; }
}

/**
 * Fetch wrapper that adds auth header.
 * Use exactly like fetch() — same signature.
 */
export async function apiFetch(urlOrPath, options = {}, _retry = false, _retry503 = 0) {
  const url = urlOrPath.startsWith('http') ? urlOrPath : `${BACKEND_URL}${urlOrPath}`;

  // A Quick View keeps nothing on the server (utils/quickViewGuard): a request
  // about one is answered here and never sent.
  if (quickViewBlocked(url, options, options.headers || {})) return quickViewResponse();

  const token = await getToken();
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Per-tab id so the live-sync can ignore this tab's OWN write echoes while
  // still syncing the same user's other tabs.
  if (!headers['X-Pidly-Client-Id']) headers['X-Pidly-Client-Id'] = getClientId();

  const { quiet: _quiet, ...fetchOptions } = options;
  let res;
  try {
    res = await fetch(url, { ...fetchOptions, headers });
  } catch (err) {
    if (isSaveRequest(url, options)) {
      reportSaveFailure({ message: 'Could not reach the server. Check your connection and try again.' });
    }
    throw err;
  }

  // Transient backend hiccup: auth returns 503 "temporarily unavailable" on a DB
  // stutter (common under load during multi-file OCR/detection runs). The 503 is
  // raised in the auth middleware BEFORE the route handler runs, so the operation
  // never executed — safe to retry any method. Back off + retry so one blip
  // doesn't fail the next document mid-run.
  if (res.status === 503 && _retry503 < 2) {
    await new Promise(r => setTimeout(r, 600 * (_retry503 + 1)));
    return apiFetch(urlOrPath, options, _retry, _retry503 + 1);
  }

  // A 401 no longer blindly signs the user out — that hair-trigger was ending
  // sessions mid-pipeline on a single transient/endpoint-specific 401. Instead
  // we try to RECOVER once and only sign out on a confirmed-dead session.
  if (res.status === 401 && token && token !== 'local-dev' && !onAuthFlowPage()) {
    const localToken = getAuthToken();
    if (localToken) {
      // Local JWT can't be refreshed — but verify it's really dead before
      // nuking the session. If it's still valid, the 401 was endpoint-specific;
      // return it to the caller and keep the user signed in.
      if (await localSessionStillValid(localToken)) return res;
      handleUnauthorized();
    } else {
      // MSAL/Azure: drop the cached token, re-acquire, and retry ONCE. Only if
      // that fresh token still 401s (or can't be acquired) is the session dead.
      if (!_retry) {
        const fresh = await refreshMsalToken();
        if (fresh) return apiFetch(urlOrPath, options, true);
      }
      handleUnauthorized();
    }
  }

  // 403 with code: 'PENDING_APPROVAL': the account itself is waiting for
  // pidly's approval (scope 'account', middleware/auth.js), or the user is
  // a member of the workspace but their status is 'pending'. Dispatch a
  // global event so the app-root overlay can show the holding screen
  // instead of leaving each page to render a generic error. Body is read
  // off a clone so the original response still flows through to whoever
  // called us.
  if (res.status === 403 && typeof window !== 'undefined') {
    res.clone().json().then(d => {
      if (d?.code === 'PENDING_APPROVAL') {
        // Try to pull the orgId out of the URL so the overlay can
        // display the workspace name. Format: /api/orgs/:orgId/...
        let orgId = null;
        const m = url.match(/\/api\/orgs\/([^/?]+)/);
        if (m) orgId = decodeURIComponent(m[1]);
        window.dispatchEvent(new CustomEvent('pidly:pending-approval', { detail: { orgId, scope: d.scope || 'workspace' } }));
      }
      // The project requires two-factor sign-in and this session has not
      // satisfied it: the code screen (components/TwoFactorGate).
      if (d?.code === 'MFA_REQUIRED') {
        window.dispatchEvent(new CustomEvent('pidly:mfa-required', { detail: d }));
      }
    }).catch(() => {});
  }

  // A write the server refused or failed: say so, with the server's reason.
  if ((res.status === 403 || res.status >= 500) && isSaveRequest(url, options)) {
    res.clone().json()
      .then(d => {
        if (d?.code === 'PENDING_APPROVAL' || d?.code === 'MFA_REQUIRED') return;
        reportSaveFailure({ message: d?.error || `The server could not save it (${res.status}).` });
      })
      .catch(() => reportSaveFailure({ message: `The server could not save it (${res.status}).` }));
  }

  // A task changed: every task list (My Tasks, the Overview card, the rail
  // badge, other tabs) refetches. One place, so no caller can forget it.
  if (res.ok && isTaskWrite(url, options.method)) notifyTasksChanged();

  return res;
}

export default apiFetch;
